import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import React from "react";
import Chart from "react-apexcharts";
import InformationCircleIcon from "../../../icons/InformationCircle";
import { BoxedLoader } from "../../BoxedLoader";
import offerTypes from "../../../utils/offerTypes";
import transport from "../../../utils/transport";
import departureCity from "../../../utils/departureCity";
import period from "../../../utils/period";
import boardType from "../../../utils/boardType";
import { useTranslation } from "react-i18next";

interface PredictedTrendsWithSelectsProps {
  categories: any;
  data: any;
  title: any;
  tooltipInfo: any;
  mealsOptions?: { value: string; label: string }[];
  offerOptions?: { value: string; label: string }[];
  transportOptions?: { value: string; label: string }[];
  offerTypeChanged?: Function;
  transportChanged?: Function;
  departureCityChanged?: Function;
  periodLengthChanged?: Function;
  mealCodeChanged?: Function;
}

const PredictedTrendsWithSelects = ({
  categories,
  data,
  title,
  tooltipInfo,
  offerTypeChanged,
  transportChanged,
  mealsOptions,
  offerOptions,
  transportOptions,
  departureCityChanged,
  periodLengthChanged,
  mealCodeChanged,
}: PredictedTrendsWithSelectsProps) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const chart: any = data
    ? {
        series: [
          {
            name: "Min",
            type: "column",
            data: data.min,
            color: "#f4b73f",
          },
          {
            name: "Max",
            type: "column",
            data: data.max,
            color: "#1e4354",
          },
          {
            name: "Avg",
            type: "line",
            data: data.avg,
            color: "#5fc1f2",
          },
        ],
        options: {
          chart: {
            background: "transparent",
            stacked: false,
            toolbar: {
              show: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          colors: ["#f4b73f", "#1e4354", "#5fc1f2"],
          grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 2,
          },
          legend: {
            position: "top",
            horizontalAlign: "right",
            labels: {
              colors: theme.palette.text.secondary,
            },
            show: true,
          },
          plotOptions: {
            bar: {
              columnWidth: "40%",
            },
          },
          stroke: {
            colors: ["transparent"],
            show: true,
            width: 2,
          },
          theme: {
            mode: theme.palette.mode,
          },
          tooltip: {
            theme: theme.palette.mode,
          },
          xaxis: {
            axisBorder: {
              show: true,
              color: theme.palette.divider,
            },
            axisTicks: {
              show: true,
              color: theme.palette.divider,
            },
            categories: categories,
            labels: {
              style: {
                colors: theme.palette.text.secondary,
              },
            },
          },
          yaxis: {
            axisBorder: {
              color: theme.palette.divider,
              show: true,
            },
            axisTicks: {
              color: theme.palette.divider,
              show: true,
            },
            labels: {
              style: {
                colors: theme.palette.text.secondary,
              },
            },
          },
        },
      }
    : {};

  return (
    <Card>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CardHeader title={title} />
        <Tooltip title={tooltipInfo}>
          <InformationCircleIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Divider />
      <CardContent>
        <Box sx={{ pt: 3 }}>
          <TextField
            label={t(`other.offer_type`)}
            name="recreation"
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{ mx: 3 }}
            value="STAY"
            onChange={(ev) =>
              offerTypeChanged && offerTypeChanged(ev.target.value)
            }
          >
            {(offerOptions || offerTypes).map((option) => (
              <option key={option.value} value={option.value}>
                {t(`utils.${option.label.toLocaleLowerCase()}`)}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`main.transporttype`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{ mx: 3 }}
            value="FLIGHT"
            onChange={(ev) =>
              transportChanged && transportChanged(ev.target.value)
            }
          >
            {(transportOptions || transport).map((option) => (
              <option key={option.value} value={option.value}>
                {t(`utils.${option.label.toLocaleLowerCase()}`)}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`main.departurecity`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
            value="Gdansk"
            onChange={(ev) =>
              departureCityChanged && departureCityChanged(ev.target.value)
            }
          >
            {departureCity.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`main.period`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
            value={7}
            onChange={(ev) =>
              periodLengthChanged && periodLengthChanged(ev.target.value)
            }
          >
            {period.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`other.board_type`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
            value="AI"
            InputLabelProps={{ shrink: true }}
            onChange={(ev) =>
              mealCodeChanged && mealCodeChanged(ev.target.value)
            }
          >
            {(mealsOptions || boardType).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        </Box>
        {!data && <BoxedLoader height={"390px"} />}
        {data && <Chart height={390} {...chart} />}
      </CardContent>
    </Card>
  );
};

export default PredictedTrendsWithSelects;
